/**
 * =====================================================
 *  WHISPERING HOMES — Custom Section Analytics (v1)
 *  File: assets/wh-analytics.js
 *
 *  Tracks:
 *    - Section card clicks (product link clicks)
 *    - Add to Cart events from custom sections
 *    - Revenue = price at time of ATC (in paise, converted to ₹ for display)
 *
 *  Storage: localStorage key "wh_analytics_events"
 *  Dashboard: window.WHAnalytics.renderDashboard(containerId)
 *
 *  Event shape stored:
 *  {
 *    type: 'click' | 'atc',
 *    section: 'more-in-style' | 'picked-for-you' | 'complete-your-decor',
 *    productHandle: string,
 *    productTitle: string,
 *    price: number,        // in paise (e.g. 149900 = ₹1499)
 *    quantity: number,     // always 1 for click, actual qty for ATC
 *    ts: number,           // Date.now()
 *    date: string,         // 'YYYY-MM-DD'
 *  }
 * =====================================================
 */

window.WHAnalytics = (() => {

  const STORAGE_KEY  = 'wh_analytics_events';
  const MAX_EVENTS   = 2000; // localStorage limit ke liye cap

  // ─────────────────────────────────────────────────────────────────
  //  STORAGE HELPERS
  // ─────────────────────────────────────────────────────────────────
  function loadEvents() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function saveEvents(events) {
    try {
      // Purane events trim karo agar limit exceed ho
      const trimmed = events.slice(-MAX_EVENTS);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } catch (e) {
      console.warn('[WHAnalytics] Could not save to localStorage:', e);
    }
  }

  // ─────────────────────────────────────────────────────────────────
  //  TRACK
  // ─────────────────────────────────────────────────────────────────
  function track(type, section, productHandle, productTitle, price, quantity) {
    if (!type || !section) return;
    const now = new Date();
    const event = {
      type,
      section,
      productHandle: productHandle || '',
      productTitle:  productTitle  || '',
      price:         parseInt(price || 0, 10),
      quantity:      parseInt(quantity || 1, 10),
      ts:            now.getTime(),
      date:          now.toISOString().split('T')[0],
    };
    const events = loadEvents();
    events.push(event);
    saveEvents(events);
  }

  // ─────────────────────────────────────────────────────────────────
  //  PUBLIC API — track click
  // ─────────────────────────────────────────────────────────────────
  function trackClick(section, productHandle, productTitle, price) {
    track('click', section, productHandle, productTitle, price, 1);
  }

  // ─────────────────────────────────────────────────────────────────
  //  PUBLIC API — track add to cart
  // ─────────────────────────────────────────────────────────────────
  function trackATC(section, productHandle, productTitle, price, quantity) {
    track('atc', section, productHandle, productTitle, price, quantity || 1);
  }

  // ─────────────────────────────────────────────────────────────────
  //  CLEAR DATA
  // ─────────────────────────────────────────────────────────────────
  function clearAll() {
    localStorage.removeItem(STORAGE_KEY);
  }

  // ─────────────────────────────────────────────────────────────────
  //  HELPERS FOR DASHBOARD
  // ─────────────────────────────────────────────────────────────────
  function formatMoney(paise) {
    return '₹' + Math.round(paise / 100).toLocaleString('en-IN');
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
  }

  const SECTION_LABELS = {
    'more-in-style':       'More In This Style',
    'picked-for-you':      'Picked For Your Taste',
    'complete-your-decor': 'Complete Your Decor Setup',
  };

  // ─────────────────────────────────────────────────────────────────
  //  COMPUTE STATS
  // ─────────────────────────────────────────────────────────────────
  function computeStats(events) {
    const sections = Object.keys(SECTION_LABELS);
    const stats = {};

    sections.forEach(s => {
      stats[s] = {
        label:      SECTION_LABELS[s],
        clicks:     0,
        atcCount:   0,
        revenue:    0,
        topProducts: {},
      };
    });

    events.forEach(e => {
      const s = stats[e.section];
      if (!s) return;
      if (e.type === 'click') {
        s.clicks++;
      } else if (e.type === 'atc') {
        s.atcCount++;
        s.revenue += (e.price * e.quantity);
        const key = e.productHandle;
        if (!s.topProducts[key]) {
          s.topProducts[key] = { title: e.productTitle, handle: key, atc: 0, revenue: 0 };
        }
        s.topProducts[key].atc++;
        s.topProducts[key].revenue += (e.price * e.quantity);
      }
    });

    // Top products sort
    sections.forEach(s => {
      stats[s].topProductsList = Object.values(stats[s].topProducts)
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);
    });

    return stats;
  }

  // ─────────────────────────────────────────────────────────────────
  //  DAILY CHART DATA
  // ─────────────────────────────────────────────────────────────────
  function getDailyData(events, days) {
    const result = {};
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      result[key] = { clicks: 0, atc: 0, revenue: 0 };
    }
    events.forEach(e => {
      if (result[e.date]) {
        if (e.type === 'click') result[e.date].clicks++;
        if (e.type === 'atc') {
          result[e.date].atc++;
          result[e.date].revenue += (e.price * e.quantity);
        }
      }
    });
    return result;
  }

  // ─────────────────────────────────────────────────────────────────
  //  RENDER DASHBOARD
  // ─────────────────────────────────────────────────────────────────
  function renderDashboard(containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;

    const events  = loadEvents();
    const stats   = computeStats(events);
    const daily7  = getDailyData(events, 7);
    const dailyDates = Object.keys(daily7);

    const totalClicks  = events.filter(e => e.type === 'click').length;
    const totalATC     = events.filter(e => e.type === 'atc').length;
    const totalRevenue = events.filter(e => e.type === 'atc').reduce((s, e) => s + e.price * e.quantity, 0);
    const convRate     = totalClicks > 0 ? ((totalATC / totalClicks) * 100).toFixed(1) : '0.0';

    const firstEvent = events[0];
    const lastEvent  = events[events.length - 1];

    // ── Bar chart max ──
    const maxRevenue = Math.max(...dailyDates.map(d => daily7[d].revenue), 1);
    const maxClicks  = Math.max(...dailyDates.map(d => daily7[d].clicks), 1);

    container.innerHTML = `
<div class="wha-dash">

  <!-- Header -->
  <div class="wha-header">
    <div>
      <h1 class="wha-title">Section Analytics</h1>
      <p class="wha-subtitle">
        ${events.length > 0
          ? `${formatDate(firstEvent?.date)} — ${formatDate(lastEvent?.date)} &nbsp;·&nbsp; ${events.length} total events`
          : 'Abhi tak koi data nahi'}
      </p>
    </div>
    <button class="wha-clear-btn" onclick="
      if(confirm('Sab data delete karna chahte ho?')) {
        window.WHAnalytics.clearAll();
        window.WHAnalytics.renderDashboard('${containerId}');
      }
    ">Clear Data</button>
  </div>

  <!-- Summary Cards -->
  <div class="wha-summary">
    <div class="wha-card">
      <span class="wha-card__label">Total Clicks</span>
      <span class="wha-card__value">${totalClicks.toLocaleString('en-IN')}</span>
    </div>
    <div class="wha-card">
      <span class="wha-card__label">Add to Carts</span>
      <span class="wha-card__value">${totalATC.toLocaleString('en-IN')}</span>
    </div>
    <div class="wha-card wha-card--highlight">
      <span class="wha-card__label">Total Revenue</span>
      <span class="wha-card__value">${formatMoney(totalRevenue)}</span>
    </div>
    <div class="wha-card">
      <span class="wha-card__label">Conv. Rate</span>
      <span class="wha-card__value">${convRate}%</span>
      <span class="wha-card__sub">clicks → ATC</span>
    </div>
  </div>

  <!-- Daily Chart — Last 7 Days -->
  <div class="wha-section-box">
    <h2 class="wha-section-title">Last 7 Days</h2>
    <div class="wha-chart">
      ${dailyDates.map(date => {
        const d = daily7[date];
        const revPct    = maxRevenue > 0 ? Math.round((d.revenue / maxRevenue) * 100) : 0;
        const clickPct  = maxClicks  > 0 ? Math.round((d.clicks  / maxClicks)  * 100) : 0;
        const label     = date.slice(5).replace('-', '/');
        return `
        <div class="wha-chart__col">
          <div class="wha-chart__bars">
            <div class="wha-chart__bar wha-chart__bar--rev"
                 style="height:${revPct}%"
                 title="Revenue: ${formatMoney(d.revenue)}"></div>
            <div class="wha-chart__bar wha-chart__bar--click"
                 style="height:${clickPct}%"
                 title="Clicks: ${d.clicks}"></div>
          </div>
          <span class="wha-chart__label">${label}</span>
          <span class="wha-chart__val">${formatMoney(d.revenue)}</span>
        </div>`;
      }).join('')}
    </div>
    <div class="wha-legend">
      <span class="wha-legend__dot wha-legend__dot--rev"></span> Revenue &nbsp;&nbsp;
      <span class="wha-legend__dot wha-legend__dot--click"></span> Clicks
    </div>
  </div>

  <!-- Per Section Stats -->
  <h2 class="wha-section-title" style="margin-top:32px;">Section Breakdown</h2>
  <div class="wha-sections">
    ${Object.entries(stats).map(([key, s]) => `
    <div class="wha-section-box">
      <div class="wha-section-header">
        <h3 class="wha-section-name">${s.label}</h3>
        <span class="wha-section-key">${key}</span>
      </div>
      <div class="wha-section-nums">
        <div class="wha-snum">
          <span class="wha-snum__val">${s.clicks}</span>
          <span class="wha-snum__label">Clicks</span>
        </div>
        <div class="wha-snum">
          <span class="wha-snum__val">${s.atcCount}</span>
          <span class="wha-snum__label">Add to Carts</span>
        </div>
        <div class="wha-snum wha-snum--highlight">
          <span class="wha-snum__val">${formatMoney(s.revenue)}</span>
          <span class="wha-snum__label">Revenue</span>
        </div>
        <div class="wha-snum">
          <span class="wha-snum__val">${s.clicks > 0 ? ((s.atcCount / s.clicks) * 100).toFixed(1) : '0.0'}%</span>
          <span class="wha-snum__label">Conv. Rate</span>
        </div>
      </div>
      ${s.topProductsList.length > 0 ? `
      <table class="wha-table">
        <thead>
          <tr>
            <th>Product</th>
            <th style="text-align:right">ATC</th>
            <th style="text-align:right">Revenue</th>
          </tr>
        </thead>
        <tbody>
          ${s.topProductsList.map(p => `
          <tr>
            <td><a href="/products/${p.handle}" target="_blank" class="wha-link">${p.title || p.handle}</a></td>
            <td style="text-align:right">${p.atc}</td>
            <td style="text-align:right">${formatMoney(p.revenue)}</td>
          </tr>`).join('')}
        </tbody>
      </table>` : `<p class="wha-empty">Abhi tak is section se koi ATC nahi hua.</p>`}
    </div>`).join('')}
  </div>

  <!-- Raw Export -->
  <div class="wha-section-box" style="margin-top:24px;">
    <h2 class="wha-section-title">Export Raw Data (CSV)</h2>
    <button class="wha-export-btn" onclick="window.WHAnalytics.exportCSV()">Download CSV</button>
    <span style="color:#999; font-size:13px; margin-left:12px;">${events.length} events</span>
  </div>

</div>`;
  }

  // ─────────────────────────────────────────────────────────────────
  //  CSV EXPORT
  // ─────────────────────────────────────────────────────────────────
  function exportCSV() {
    const events = loadEvents();
    if (!events.length) { alert('Koi data nahi hai.'); return; }

    const headers = ['Date', 'Type', 'Section', 'Product Handle', 'Product Title', 'Price (₹)', 'Quantity', 'Revenue (₹)', 'Timestamp'];
    const rows = events.map(e => [
      e.date,
      e.type,
      SECTION_LABELS[e.section] || e.section,
      e.productHandle,
      `"${(e.productTitle || '').replace(/"/g, '""')}"`,
      (e.price / 100).toFixed(2),
      e.quantity,
      ((e.price * e.quantity) / 100).toFixed(2),
      new Date(e.ts).toISOString(),
    ].join(','));

    const csv  = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `wh-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ─────────────────────────────────────────────────────────────────
  //  INJECT DASHBOARD CSS
  // ─────────────────────────────────────────────────────────────────
  function injectDashboardStyles() {
    if (document.getElementById('wha-styles')) return;
    const s = document.createElement('style');
    s.id = 'wha-styles';
    s.textContent = `
.wha-dash {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  max-width: 1000px;
  margin: 0 auto;
  padding: 24px 16px 60px;
  color: #1a1a1a;
}
.wha-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 28px;
}
.wha-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 4px;
  color: #2b1d0a;
}
.wha-subtitle {
  font-size: 13px;
  color: #777;
  margin: 0;
}
.wha-clear-btn {
  background: #fff;
  border: 1.5px solid #e0d6cc;
  color: #c0392b;
  font-size: 13px;
  padding: 7px 16px;
  border-radius: 6px;
  cursor: pointer;
}
.wha-clear-btn:hover { background: #fef2f2; }
.wha-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px;
  margin-bottom: 28px;
}
.wha-card {
  background: #fff;
  border: 1px solid #e5e0d8;
  border-radius: 10px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.wha-card--highlight {
  background: #2b1d0a;
  border-color: #2b1d0a;
}
.wha-card--highlight .wha-card__label,
.wha-card--highlight .wha-card__value { color: #fff; }
.wha-card__label { font-size: 12px; color: #888; text-transform: uppercase; letter-spacing: .5px; }
.wha-card__value { font-size: 26px; font-weight: 700; color: #2b1d0a; line-height: 1.1; }
.wha-card__sub { font-size: 11px; color: #aaa; }
.wha-section-box {
  background: #fff;
  border: 1px solid #e5e0d8;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 16px;
}
.wha-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2b1d0a;
  margin: 0 0 16px;
}
/* Chart */
.wha-chart {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 120px;
  margin-bottom: 8px;
}
.wha-chart__col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  height: 100%;
}
.wha-chart__bars {
  flex: 1;
  width: 100%;
  display: flex;
  align-items: flex-end;
  gap: 2px;
  min-height: 0;
}
.wha-chart__bar {
  flex: 1;
  border-radius: 3px 3px 0 0;
  min-height: 2px;
  transition: opacity .2s;
  cursor: default;
}
.wha-chart__bar:hover { opacity: .75; }
.wha-chart__bar--rev   { background: #2b1d0a; }
.wha-chart__bar--click { background: #c8b89a; }
.wha-chart__label { font-size: 10px; color: #aaa; }
.wha-chart__val { font-size: 9px; color: #888; }
.wha-legend { display: flex; align-items: center; font-size: 12px; color: #777; gap: 4px; }
.wha-legend__dot { display: inline-block; width: 10px; height: 10px; border-radius: 2px; }
.wha-legend__dot--rev   { background: #2b1d0a; }
.wha-legend__dot--click { background: #c8b89a; }
/* Sections */
.wha-sections { display: grid; gap: 16px; }
.wha-section-header { display: flex; align-items: baseline; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
.wha-section-name { font-size: 15px; font-weight: 600; margin: 0; color: #2b1d0a; }
.wha-section-key { font-size: 11px; color: #aaa; font-family: monospace; background: #f5f2ee; padding: 2px 6px; border-radius: 4px; }
.wha-section-nums { display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 16px; }
.wha-snum { display: flex; flex-direction: column; gap: 2px; }
.wha-snum__val { font-size: 20px; font-weight: 700; color: #2b1d0a; }
.wha-snum--highlight .wha-snum__val { color: #1a6b3e; }
.wha-snum__label { font-size: 11px; color: #888; }
/* Table */
.wha-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.wha-table th { text-align: left; font-weight: 600; color: #888; font-size: 11px; text-transform: uppercase; letter-spacing: .4px; padding: 6px 8px; border-bottom: 1px solid #f0ebe4; }
.wha-table td { padding: 8px 8px; border-bottom: 1px solid #f7f4f0; color: #333; }
.wha-table tr:last-child td { border-bottom: none; }
.wha-link { color: #2b1d0a; text-decoration: underline; }
.wha-empty { color: #aaa; font-size: 13px; margin: 8px 0 0; }
.wha-export-btn {
  background: #2b1d0a;
  color: #fff;
  border: none;
  padding: 9px 20px;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}
.wha-export-btn:hover { background: #3d2c16; }
@media (max-width: 500px) {
  .wha-chart__val { display: none; }
  .wha-section-nums { gap: 14px; }
  .wha-snum__val { font-size: 17px; }
}
    `;
    document.head.appendChild(s);
  }

  // Auto inject styles
  injectDashboardStyles();

  return {
    trackClick,
    trackATC,
    clearAll,
    exportCSV,
    renderDashboard,
    loadEvents,
  };

})();